import { cn } from "@/lib/utils";
import {
  BookPlus,
  BoxSelect,
  Component,
  Copy,
  FileCode2,
  FileMinus,
  FilePen,
  FilePlus,
  FolderMinus,
  FolderPlus,
  Frame,
  Hexagon,
  Library,
  Loader2,
  PackageCheck,
  PackageMinus,
  PackagePlus,
  Palette,
  RefreshCcwIcon,
  Save,
  SaveIcon,
  SendToBack,
  Sparkles,
  Trash,
  WholeWord,
  type LucideIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import useActivityStore from "@/store/zustand/activityStore";
import { useDispatch } from "react-redux";
import { useCanvasId } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import { useIsChangePending } from "@/store/zustand/designCommandManagerStore";

interface Props {
  timestamp: string;
  changeUndone: boolean;
  changeType: ActivityChangeType;
  selected: boolean;
  onSelect: () => void;
  isCurrentActivity: boolean;
  activityId: string;
}

// Must match in tempo-web
export enum ActivityChangeType {
  SAVE_FILE = "SAVE_FILE",
  ADD_COMPONENT = "ADD_COMPONENT",
  CHANGE_STYLING_REACT = "CHANGE_STYLING_REACT",
  UPDATE_STORYBOARDS = "UPDATE_STORYBOARDS",
  CHANGE_STYLING_CSS = "CHANGE_STYLING_CSS",
  ADD_CLASS = "ADD_CLASS",
  REMOVE_CLASS = "REMOVE_CLASS",
  WRAP_IN_DIV = "WRAP_IN_DIV",
  DUPLICATE = "DUPLICATE",
  CREATE_CSS_RULE = "CREATE_CSS_RULE",
  DELETE_CSS_RULE = "DELETE_CSS_RULE",
  ADD_JSX_ELEMENT = "ADD_JSX_ELEMENT",
  MOVE_JSX_ELEMENT = "MOVE_JSX_ELEMENT",
  ADD_AND_REMOVE_JSX_ELEMENT = "ADD_AND_REMOVE_JSX_ELEMENT",
  DETACH_INSTANCE = "DETACH_INSTANCE",
  REMOVE_JSX_ELEMENT = "REMOVE_JSX_ELEMENT",
  CHANGE_PROP = "CHANGE_PROP",
  CREATE_PAGE = "CREATE_PAGE",
  DELETE_PAGE = "DELETE_PAGE",
  DUPLICATE_PAGE = "DUPLICATE_PAGE",
  PERSIST_PAGE = "PERSIST_PAGE",
  UPDATE_PAGE = "UPDATE_PAGE",
  UNPERSIST_PAGE = "UNPERSIST_PAGE",
  CHANGE_ELEMENT_TAG = "CHANGE_ELEMENT_TAG",
  CREATE_UNSAVED_COMPONENT = "CREATE_UNSAVED_COMPONENT",
  DUPLICATE_UNSAVED_COMPONENT = "DUPLICATE_UNSAVED_COMPONENT",
  PASTE_STORYBOARDS = "PASTE_STORYBOARDS",
  DELETE_UNSAVED_COMPONENT = "DELETE_UNSAVED_COMPONENT",
  SAVE_UNSAVED_COMPONENT = "SAVE_UNSAVED_COMPONENT",
  SAVE_UNSAVED_STORY = "SAVE_UNSAVED_STORY",
  AI_CHANGE = "AI_CHANGE",
  AI_VISUAL_COPILOT_CHANGE = "AI_VISUAL_COPILOT_CHANGE",
  AI_GENERATE_COMPONENT = "AI_GENERATE_COMPONENT",
  AI_EDIT_COMPONENT = "AI_EDIT_COMPONENT",
  AI_GENERATE_IMAGE = "AI_GENERATE_IMAGE",
  CREATE_FILE = "CREATE_FILE",
  CREATE_FOLDER = "CREATE_FOLDER",
  REMOVE_FILE = "REMOVE_FILE",
  REMOVE_FOLDER = "REMOVE_FOLDER",
  EDIT_TEXT = "EDIT_TEXT",
  RENAME_FILE = "RENAME_FILE",
  GIT_COMMIT_HOOK = "GIT_COMMIT_HOOK",
  UPDATE_TAILWIND_CONFIG = "UPDATE_TAILWIND_CONFIG",
  AI_CREATE_WIREFRAME = "AI_CREATE_WIREFRAME",
  CREATE_STORY = "CREATE_STORY",
  ADD_LIBRARY_COMPONENT = "ADD_LIBRARY_COMPONENT",
  REMOVE_LIBRARY_COMPONENT = "REMOVE_LIBRARY_COMPONENT",
}

export const ActivityChangeTypeToName: { [key in ActivityChangeType]: string } =
  {
    [ActivityChangeType.SAVE_FILE]: "Save File",
    [ActivityChangeType.UPDATE_STORYBOARDS]: "Update Storyboard",
    [ActivityChangeType.ADD_COMPONENT]: "Add Marketplace Component",
    [ActivityChangeType.CHANGE_STYLING_REACT]: "Style Change in React Element",
    [ActivityChangeType.CHANGE_STYLING_CSS]: "Style Change in CSS Rule",
    [ActivityChangeType.ADD_CLASS]: "Add Class to Element",
    [ActivityChangeType.REMOVE_CLASS]: "Remove Class from Element",
    [ActivityChangeType.WRAP_IN_DIV]: "Wrap in Div",
    [ActivityChangeType.DUPLICATE]: "Duplicate",
    [ActivityChangeType.CREATE_CSS_RULE]: "Create CSS Rule",
    [ActivityChangeType.DELETE_CSS_RULE]: "Delete CSS Rule",
    [ActivityChangeType.ADD_JSX_ELEMENT]: "Add Element",
    [ActivityChangeType.MOVE_JSX_ELEMENT]: "Move Element",
    [ActivityChangeType.ADD_AND_REMOVE_JSX_ELEMENT]: "Move Element",
    [ActivityChangeType.DETACH_INSTANCE]: "Detach Instance",
    [ActivityChangeType.REMOVE_JSX_ELEMENT]: "Delete Element",
    [ActivityChangeType.CHANGE_PROP]: "Change Prop",
    [ActivityChangeType.CHANGE_ELEMENT_TAG]: "Change Element Tag",
    [ActivityChangeType.CREATE_UNSAVED_COMPONENT]: "Create New Storyboard",
    [ActivityChangeType.DUPLICATE_UNSAVED_COMPONENT]: "Duplicate Storyboard",
    [ActivityChangeType.PASTE_STORYBOARDS]: "Paste Storyboards",
    [ActivityChangeType.DELETE_UNSAVED_COMPONENT]: "Delete Storyboard",
    [ActivityChangeType.SAVE_UNSAVED_COMPONENT]: "Save Storyboard as Component",
    [ActivityChangeType.SAVE_UNSAVED_STORY]: "Save Storyboard as Story",
    [ActivityChangeType.AI_CHANGE]: "AI Edit File",
    [ActivityChangeType.AI_VISUAL_COPILOT_CHANGE]: "Eng Spec Change",
    [ActivityChangeType.AI_GENERATE_COMPONENT]: "AI Generate Component",
    [ActivityChangeType.AI_EDIT_COMPONENT]: "AI Edit Component",
    [ActivityChangeType.CREATE_FILE]: "Create File",
    [ActivityChangeType.CREATE_FOLDER]: "Create Folder",
    [ActivityChangeType.REMOVE_FILE]: "Remove File",
    [ActivityChangeType.REMOVE_FOLDER]: "Remove Folder",
    [ActivityChangeType.EDIT_TEXT]: "Edit Text",
    [ActivityChangeType.RENAME_FILE]: "Rename File",
    [ActivityChangeType.GIT_COMMIT_HOOK]: "Changes from Git Commit Hook",
    [ActivityChangeType.UPDATE_TAILWIND_CONFIG]: "Update Design Tokens",
    [ActivityChangeType.AI_GENERATE_IMAGE]: "AI Generate Image",
    [ActivityChangeType.AI_CREATE_WIREFRAME]: "AI Create Wireframe",
    [ActivityChangeType.CREATE_STORY]: "Create Story",
    [ActivityChangeType.CREATE_PAGE]: "Create Page",
    [ActivityChangeType.DELETE_PAGE]: "Delete Page",
    [ActivityChangeType.DUPLICATE_PAGE]: "Duplicate Page",
    [ActivityChangeType.PERSIST_PAGE]: "Save Page to Codebase",
    [ActivityChangeType.UNPERSIST_PAGE]: "Remove Page from Codebase",
    [ActivityChangeType.UPDATE_PAGE]: "Update Page",
    [ActivityChangeType.ADD_LIBRARY_COMPONENT]: "Add to Library",
    [ActivityChangeType.REMOVE_LIBRARY_COMPONENT]: "Remove from Library",
  };

export const ActivityChangeTypeToIcon: Partial<
  Record<ActivityChangeType, LucideIcon>
> = {
  [ActivityChangeType.SAVE_FILE]: SaveIcon,
  [ActivityChangeType.UPDATE_STORYBOARDS]: PackageCheck,
  [ActivityChangeType.ADD_COMPONENT]: Component,
  [ActivityChangeType.CHANGE_STYLING_REACT]: Palette,
  [ActivityChangeType.CHANGE_STYLING_CSS]: Palette,
  [ActivityChangeType.ADD_CLASS]: Palette,
  [ActivityChangeType.REMOVE_CLASS]: Palette,
  [ActivityChangeType.WRAP_IN_DIV]: BoxSelect,
  [ActivityChangeType.DUPLICATE]: Copy,
  [ActivityChangeType.ADD_JSX_ELEMENT]: PackagePlus,
  [ActivityChangeType.MOVE_JSX_ELEMENT]: SendToBack,
  [ActivityChangeType.ADD_AND_REMOVE_JSX_ELEMENT]: SendToBack,
  [ActivityChangeType.DETACH_INSTANCE]: SendToBack,
  [ActivityChangeType.REMOVE_JSX_ELEMENT]: PackageMinus,
  [ActivityChangeType.CHANGE_ELEMENT_TAG]: FileCode2,
  [ActivityChangeType.CREATE_UNSAVED_COMPONENT]: Frame,
  [ActivityChangeType.DUPLICATE_UNSAVED_COMPONENT]: Copy,
  [ActivityChangeType.PASTE_STORYBOARDS]: Frame,
  [ActivityChangeType.DELETE_UNSAVED_COMPONENT]: Trash,
  [ActivityChangeType.SAVE_UNSAVED_COMPONENT]: Save,
  [ActivityChangeType.SAVE_UNSAVED_STORY]: Save,
  [ActivityChangeType.AI_CHANGE]: Sparkles,
  [ActivityChangeType.AI_VISUAL_COPILOT_CHANGE]: Sparkles,
  [ActivityChangeType.AI_GENERATE_COMPONENT]: Sparkles,
  [ActivityChangeType.AI_EDIT_COMPONENT]: Sparkles,
  [ActivityChangeType.CREATE_FILE]: FilePlus,
  [ActivityChangeType.CREATE_FOLDER]: FolderPlus,
  [ActivityChangeType.REMOVE_FILE]: FileMinus,
  [ActivityChangeType.REMOVE_FOLDER]: FolderMinus,
  [ActivityChangeType.EDIT_TEXT]: WholeWord,
  [ActivityChangeType.CHANGE_PROP]: Component,
  [ActivityChangeType.RENAME_FILE]: FilePen,
  [ActivityChangeType.AI_GENERATE_IMAGE]: Sparkles,
  [ActivityChangeType.GIT_COMMIT_HOOK]: PackageCheck,
  [ActivityChangeType.UPDATE_TAILWIND_CONFIG]: Palette,
  [ActivityChangeType.AI_CREATE_WIREFRAME]: Sparkles,
  [ActivityChangeType.CREATE_STORY]: BookPlus,
  [ActivityChangeType.CREATE_PAGE]: Sparkles,
  [ActivityChangeType.DELETE_PAGE]: Trash,
  [ActivityChangeType.DUPLICATE_PAGE]: Copy,
  [ActivityChangeType.PERSIST_PAGE]: Save,
  [ActivityChangeType.UPDATE_PAGE]: Sparkles,
  [ActivityChangeType.ADD_LIBRARY_COMPONENT]: Library,
  [ActivityChangeType.REMOVE_LIBRARY_COMPONENT]: Library,
};

const ActivityIcon: React.FC<{ changeType: ActivityChangeType }> = ({
  changeType,
}) => {
  const defaultIcon: LucideIcon = Hexagon;
  const Icon = ActivityChangeTypeToIcon[changeType] || defaultIcon;
  return <Icon className="w-4 h-4" />;
};

const ActivityStreamItem: React.FC<Props> = (props) => {
  const canvasId = useCanvasId();

  const dispatch = useDispatch();

  const [hovering, setHovering] = useState(false);

  const activityStreamList = useActivityStore(
    (state) => state.activityStreamList,
  );

  const { restoreActivity, restoringActivityId } = useActivityStore();

  const isChangePending = useIsChangePending();

  const { session } = useSession();

  const formattedDate = useMemo(() => {
    const date = new Date(props.timestamp);
    // Correctly specify the options with specific string literals
    const optionsDate: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
    };
    const optionsTime: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    const formattedDate = `${date.toLocaleDateString(
      "en-US",
      optionsDate,
    )}, ${date.toLocaleTimeString("en-US", optionsTime)}`;

    return formattedDate;
  }, [props.timestamp]);

  const handleClick = async () => {
    props.onSelect();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={cn(
        "flex flex-row justify-start items-center pl-4 pr-2 h-14 w-full cursor-pointer",
        props.selected
          ? "bg-primary-0 hover:bg-primary-100"
          : props.changeUndone
            ? "bg-secondary-200 hover:bg-secondary-300"
            : "bg-secondary-0 hover:bg-secondary-100",
      )}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div
        className={cn(
          "flex flex-col text-left min-w-0 rounded-none pl-0 justify-center items-start space-y-1 grow shrink",
        )}
      >
        <div className="flex flex-row justify-center items-center space-x-1 overflow-hidden min-w-0 w-full">
          <ActivityIcon changeType={props.changeType} />
          <div className="font-inter whitespace-nowrap truncate text-left normal-case text-xs font-medium grow shrink block">
            {ActivityChangeTypeToName[props.changeType]}
          </div>
        </div>
        <div
          className={cn(
            "font-inter text-left w-full normal-case text-xs",
            props.selected ? "text-white" : "text-muted-foreground",
          )}
        >
          {formattedDate}
        </div>
      </div>
      <div className="shrink-0">
        <div
          className={cn(
            "text-2xs rounded-xl px-2.5 py-0.5 bg-background-tertiary",
            !props.isCurrentActivity && !props.changeUndone && "hidden",
            props.changeUndone && "bg-destructive",
            hovering && !props.isCurrentActivity && "hidden",
            restoringActivityId === props.activityId && "hidden",
          )}
        >
          {props.isCurrentActivity ? "Current" : "Reverted"}
        </div>
        <Button
          variant="outline"
          size="xs"
          className={cn(
            "py-1 px-1.5 gap-1 text-2x rounded-md",
            !(hovering || restoringActivityId === props.activityId) && "hidden",
            props.isCurrentActivity && "hidden",
            restoringActivityId &&
              restoringActivityId !== props.activityId &&
              "hidden",
            isChangePending && "hidden",
          )}
          onClick={(e) => {
            if (!session) return;

            e.stopPropagation();
            e.preventDefault();
            restoreActivity(
              dispatch,
              canvasId,
              props.activityId,
              activityStreamList,
              session,
            );
          }}
        >
          {restoringActivityId === props.activityId ? (
            <div className={cn("animate-spin")}>
              <Loader2 className="w-3 h-3" />
            </div>
          ) : (
            <RefreshCcwIcon className="w-3 h-3" />
          )}
          <span>Restore</span>
        </Button>
      </div>
    </div>
  );
};

export default ActivityStreamItem;
